<script>
export default {
  name: "EvolucoesRelatedBlock",
  props: {
    evolucoes: { type: Array, default: () => [] },
    evolucoesLoading: { type: Boolean, default: false },
    evolucoesError: { type: [String, null], default: null },
  },
  computed: {
    evolucoesAutorizadasComParecer() {
      return (this.evolucoes || []).filter((rec) => {
        // Mantém a mesma regra do backend (trim e > 2 caracteres úteis).
        const hasDesc = String(rec?.desc ?? "")
          .trim()
          .length > 2;

        return (
          hasDesc &&
          rec?.is_authorized === true &&
          rec?.professional_aware === true
        );
      });
    },
    totalServiceValue() {
      return (this.evolucoesAutorizadasComParecer || []).reduce((acc, rec) => {
        const num = this.parseServiceValue(rec?.service_value);
        return acc + (Number.isFinite(num) ? num : 0);
      }, 0);
    },
    patientGroups() {
      // Agrupa as evoluções por paciente (resource: `pacient`)
      const map = new Map();

      (this.evolucoesAutorizadasComParecer || []).forEach((rec) => {
        const pacient = rec?.pacient || "Sem paciente";
        const key = pacient;

        if (!map.has(key)) {
          map.set(key, {
            key,
            pacient,
            items: [],
            count: 0,
            totalValue: 0,
          });
        }

        const group = map.get(key);
        group.items.push(rec);
        group.count += 1;

        const num = this.parseServiceValue(rec?.service_value);
        group.totalValue += Number.isFinite(num) ? num : 0;
      });

      // Ordena por totalValue (desc) e depois por quantidade (desc)
      return Array.from(map.values()).sort((a, b) => {
        if (b.totalValue !== a.totalValue) return b.totalValue - a.totalValue;
        return b.count - a.count;
      });
    },
  },
  methods: {
    parseServiceValue(value) {
      const v = value;
      // `service_value` chega como number no JSON real (ex.: 76), mas mantemos fallback caso venha string.
      if (typeof v === "number") return Number.isFinite(v) ? v : 0;
      if (typeof v === "string") return Number(v);
      return 0;
    },
    formatCurrencyBR(value) {
      const num = this.parseServiceValue(value);
      if (!Number.isFinite(num)) return "-";
      return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDateBR(value) {
      if (!value) return "-";
      try {
        const d = new Date(value);
        return d.toLocaleDateString("pt-BR");
      } catch (e) {
        return String(value);
      }
    },
    recurrenceVariant(rec) {
      return rec?.is_cancelled ? "danger" : "success";
    },
    recurrenceStatusText(rec) {
      return rec?.is_cancelled ? "Cancelada" : "Ativa";
    },
    /**
     * Sanitiza HTML vindo do backend para reduzir risco de XSS.
     * Mantém marcação básica, removendo tags/permissões perigosas.
     */
    sanitizeHtml(html) {
      const raw = String(html ?? "");
      if (!raw) return "";

      // Fallback (ambiente sem DOMParser): remove tags e mantém apenas texto.
      if (typeof DOMParser === "undefined") {
        return raw.replace(/<[^>]+>/g, "");
      }

      const doc = new DOMParser().parseFromString(raw, "text/html");

      // Remove nós/tags perigosas
      const dangerousTags = [
        "script",
        "iframe",
        "object",
        "embed",
        "link",
        "meta",
        "style",
      ];
      dangerousTags.forEach((tag) => {
        doc.querySelectorAll(tag).forEach((el) => el.remove());
      });

      // Remove atributos perigosos (on*, style inline, javascript:)
      doc.querySelectorAll("*").forEach((el) => {
        [...el.attributes].forEach((attr) => {
          const name = (attr.name || "").toLowerCase();
          const value = attr.value || "";

          if (name.startsWith("on")) {
            el.removeAttribute(attr.name);
            return;
          }

          // Remove inline styles
          if (name === "style") {
            el.removeAttribute(attr.name);
            return;
          }

          // Bloqueia javascript: em href/src
          if (
            (name === "href" || name === "src") &&
            String(value).trim().toLowerCase().startsWith("javascript:")
          ) {
            el.removeAttribute(attr.name);
            return;
          }
        });
      });

      return doc.body.innerHTML;
    },
  },
};
</script>

<template>
  <div class="mb-4">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div class="text-muted small">Evoluções relacionadas</div>
      <div v-if="evolucoesLoading" class="text-muted small">Carregando...</div>
    </div>

    <div v-if="evolucoesError" class="alert alert-danger py-2 mb-0">
      {{ evolucoesError }}
    </div>

    <div v-else>
      <div
          v-if="!evolucoesLoading && (!evolucoesAutorizadasComParecer || evolucoesAutorizadasComParecer.length === 0)"
        class="alert alert-light border py-2 mb-0"
      >
          Nenhuma evolução autorizada com parecer preenchido (>= 3 caracteres) para esta conta.
      </div>

        <div v-if="evolucoesAutorizadasComParecer && evolucoesAutorizadasComParecer.length" class="mb-3">
        <div class="alert alert-light border py-2 mb-0">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <div class="font-weight-semibold">
                Resumo por paciente
              </div>
              <div class="small text-muted">
                  {{ patientGroups.length }} paciente(s) · {{ evolucoesAutorizadasComParecer.length }} evolução(ões)
              </div>
            </div>
            <div class="text-success font-weight-semibold">
              Total: {{ formatCurrencyBR(totalServiceValue) }}
            </div>
          </div>
          <div class="small text-muted mt-1">
            (Total calculado somando `service_value` das evoluções vinculadas)
          </div>
        </div>
      </div>

        <div v-if="evolucoesAutorizadasComParecer && evolucoesAutorizadasComParecer.length">
        <div
          v-for="(group, idx) in patientGroups"
          :key="group.key"
          class="mb-2"
        >
          <b-button
            v-b-toggle="'collapse-evo-paciente-' + idx"
            variant="outline-primary"
            block
            class="text-left"
          >
            <div class="d-flex justify-content-between align-items-center w-100">
              <div>
                <div class="font-weight-semibold">
                  {{ group.pacient }}
                </div>
                <div class="small text-muted">
                  {{ group.count }} evolução(ões)
                </div>
              </div>
              <div class="text-primary font-weight-semibold">
                {{ formatCurrencyBR(group.totalValue) }}
              </div>
            </div>
          </b-button>

          <b-collapse
            :id="'collapse-evo-paciente-' + idx"
            class="mt-2"
          >
            <ul class="list-group evolution-recurrence-list">
              <li
                v-for="rec in group.items"
                :key="rec.id"
                class="list-group-item"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <div class="font-weight-semibold">
                      {{ formatDateBR(rec.start) }}
                    </div>
                    <div class="small text-muted" v-if="rec.start_time">
                      Hora: {{ rec.start_time }}
                    </div>
                  </div>
                  <div class="text-right">
                    <b-badge :variant="recurrenceVariant(rec)">
                      {{ recurrenceStatusText(rec) }}
                    </b-badge>
                    <div class="small text-success font-weight-semibold mt-1">
                      Valor: {{ formatCurrencyBR(rec.service_value) }}
                    </div>
                  </div>
                </div>

                <div
                  class="mt-2"
                  v-if="rec.desc"
                  v-html="sanitizeHtml(rec.desc)"
                ></div>
                <div v-else class="text-muted mt-2">Sem parecer/descrição</div>
              </li>
            </ul>
          </b-collapse>
        </div>
      </div>
    </div>
  </div>
</template>

